import React from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';

const TEXT_EXTS = new Set(['csv', 'txt', 'json', 'md', 'tsv', 'xml', 'html']);

export function fileExt(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

export function isTextUploadFile(file: File): boolean {
  if (file.type.startsWith('text/') || file.type === 'application/json') return true;
  return TEXT_EXTS.has(fileExt(file.name));
}

export function acceptIncludesImages(accept?: string): boolean {
  if (!accept) return false;
  return /png|jpe?g|webp|gif|bmp|image\//i.test(accept);
}

export function formatAcceptLabel(accept?: string): string {
  if (!accept) return 'Any file';
  const parts = accept.split(',').map((s) => s.trim()).filter(Boolean);
  const labels = parts.map((p) => {
    if (p === 'image/*') return 'images';
    if (p === 'video/*') return 'video';
    if (p === 'application/pdf') return 'PDF';
    return p.replace(/^\./, '').toUpperCase();
  });
  const unique = Array.from(new Set(labels));
  if (unique.length <= 4) return unique.join(', ');
  return `${unique.slice(0, 3).join(', ')} +${unique.length - 3}`;
}

function mergeAccept(...values: Array<string | undefined>): string | undefined {
  const parts = new Set<string>();
  for (const value of values) {
    if (!value) continue;
    for (const bit of value.split(',')) {
      const trimmed = bit.trim();
      if (trimmed) parts.add(trimmed);
    }
  }
  return parts.size ? Array.from(parts).join(',') : undefined;
}

function stripBoilerplateLabel(label?: string): string | undefined {
  if (!label) return undefined;
  const cleaned = label
    .replace(/^\s*(paste|or\s+upload)\s+/i, '')
    .replace(/\s*\(optional\)\s*$/i, '')
    .trim();
  return cleaned || undefined;
}

/**
 * Collapse a textarea+file pair into one `upload` field, and normalize
 * `file` / textarea-with-accept into the same shape.
 */
export function normalizeRuntimeUploadFields(fields: any[]): any[] {
  const out: any[] = [];
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    const next = fields[i + 1];
    const isTextSide =
      field?.type === 'upload' ||
      (field?.type === 'textarea' &&
        (field.accept || field.allow_file || field.key === 'source_text'));

    if (isTextSide && next?.type === 'file') {
      out.push({
        ...field,
        type: 'upload',
        label: field.group_label || next.group_label || 'Source',
        placeholder: field.placeholder || next.placeholder,
        paste: field.paste !== false,
        accept: mergeAccept(field.accept, next.accept),
        max_files: Number(next.max_files || field.max_files || 1),
        max_size_mb: Number(next.max_size_mb || field.max_size_mb || 100),
        text_key: field.text_key || field.key,
        file_key: next.file_key || next.key,
      });
      i += 1;
      continue;
    }

    if (field?.type === 'upload' || field?.type === 'file' || isTextSide) {
      const isFileOnly = field.type === 'file';
      out.push({
        ...field,
        type: 'upload',
        label: stripBoilerplateLabel(field.label) || field.label || 'Source',
        paste: isFileOnly ? false : field.paste !== false,
        accept: field.accept,
        max_files: Number(field.max_files || 1),
        max_size_mb: Number(field.max_size_mb || (isFileOnly ? 100 : 25)),
        text_key: field.text_key || (isFileOnly ? undefined : field.key),
        file_key: field.file_key || (isFileOnly ? field.key : undefined),
      });
      continue;
    }

    out.push(field);
  }
  return out;
}

export interface RuntimeUploadFieldProps {
  label: string;
  required?: boolean;
  placeholder?: string;
  accept?: string;
  maxFiles?: number;
  paste?: boolean;
  maxSizeMb?: number;
  textValue: string;
  files: File[];
  onTextChange: (text: string) => void;
  onFilesChange: (files: File[]) => void;
  onError?: (message: string) => void;
}

export function RuntimeUploadField({
  label,
  required,
  placeholder,
  accept,
  maxFiles = 1,
  paste = true,
  maxSizeMb = 100,
  textValue,
  files,
  onTextChange,
  onFilesChange,
  onError,
}: RuntimeUploadFieldProps) {
  const inputId = `runtime-upload-${label.replace(/\W+/g, '-').toLowerCase()}`;
  const multiple = maxFiles > 1;
  const hasFiles = files.length > 0;
  const acceptHint = formatAcceptLabel(accept);

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const incoming = Array.from(list);
    const next: File[] = multiple ? [...files] : [];
    for (const file of incoming) {
      if (next.length >= maxFiles) {
        onError?.(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'}.`);
        break;
      }
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > maxSizeMb) {
        onError?.(`${file.name} is ${sizeMb.toFixed(1)} MB — maximum is ${maxSizeMb} MB.`);
        continue;
      }
      next.push(file);
    }
    onFilesChange(next);

    const textFiles = next.filter(isTextUploadFile);
    if (textFiles.length > 0) {
      Promise.all(textFiles.map((f) => f.text()))
        .then((texts) => onTextChange(texts.join('\n\n')))
        .catch(() => onError?.('Could not read that file as text'));
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
        {label}
        {required ? '' : (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 400 }}>
            (optional)
          </Typography>
        )}
      </Typography>

      <Box
        component="label"
        htmlFor={inputId}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          border: '1px dashed',
          borderColor: hasFiles ? 'success.main' : 'divider',
          borderRadius: 1.5,
          bgcolor: hasFiles ? 'rgba(46, 125, 50, 0.04)' : 'grey.50',
          cursor: 'pointer',
          textAlign: 'center',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.04)' },
        }}
      >
        <input
          id={inputId}
          type="file"
          hidden
          accept={accept || undefined}
          multiple={multiple}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <AttachFileIcon sx={{ color: hasFiles ? 'success.main' : 'text.secondary', mb: 0.5 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {hasFiles ? (multiple ? 'Add or replace files' : 'Replace file') : 'Drop or choose a file'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {acceptHint}
          {maxFiles > 1 ? ` · up to ${maxFiles}` : ''}
          {` · max ${maxSizeMb} MB`}
        </Typography>
      </Box>

      {hasFiles && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {files.map((file, idx) => (
            <Chip
              key={`${file.name}-${idx}`}
              size="small"
              label={`${file.name} (${file.size > 1024 * 1024 ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`})`}
              onDelete={() => onFilesChange(files.filter((_, i) => i !== idx))}
            />
          ))}
        </Box>
      )}

      {paste && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 1.25 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5 }}>
              or paste
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder={placeholder || 'Paste text here'}
            required={required && !hasFiles}
            value={textValue}
            onChange={(e) => onTextChange(e.target.value)}
          />
        </>
      )}
    </Box>
  );
}